/**
 * Staged-send planner service. Calls the `plan-staged-send` edge function, which
 * splits a large STATIC source list into disjoint, wave-sized static lists
 * (server-side, service-role, raised timeout). Each returned wave is a normal
 * static list the operator then sends as its own flight.
 */
import { supabase } from "./supabase";

export interface StagedSendWave {
  wave_index: number;
  list_id: string;
  contact_count: number;
}

export interface StagedSendResult {
  waves: StagedSendWave[];
  total: number;
}

export async function planStagedSend(
  sourceListId: string,
  waveSizes: number[],
  label: string,
): Promise<StagedSendResult> {
  const { data, error } = await supabase.functions.invoke("plan-staged-send", {
    body: {
      source_list_id: sourceListId,
      wave_sizes: waveSizes,
      label,
    },
  });

  if (error) {
    // supabase-js puts a non-2xx response body on error.context (a Response),
    // NOT on `data` — read the server's { error } message from there so the
    // operator sees "source list must be STATIC…" instead of a generic failure.
    let message = error.message;
    try {
      const body = await (
        error as { context?: { json?: () => Promise<{ error?: string }> } }
      ).context?.json?.();
      if (body?.error) {
        message = body.error;
      }
    } catch {
      /* fall through to the generic message */
    }
    throw new Error(message);
  }

  if (!data?.success) {
    throw new Error(data?.error || "Failed to plan the staged send.");
  }

  return { waves: data.waves ?? [], total: data.total ?? 0 };
}
